"use client";

import Image from "next/image";
import Link from "next/link";
import {
  ArrowLeftIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MapPinIcon,
  ClockIcon,
  ShareIcon,
  HeartIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/solid";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import type { RemoteCompany } from "@/types";
import { resolveImageUrl } from "@/lib/api";

type CompanyMobileViewProps = {
  company: RemoteCompany;
  formattedHours: string | null;
};

// Helper function to add UTM parameters to website URL
function addUtmToWebsite(website: string): string {
  const baseUrl = website.startsWith("http") ? website : `https://${website}`;
  const url = new URL(baseUrl);
  url.searchParams.set("utm_source", "santafeconecta.com.br");
  url.searchParams.set("utm_medium", "referral");
  url.searchParams.set("utm_campaign", "empresa_perfil");
  return url.toString();
}

export default function CompanyMobileView({
  company,
  formattedHours,
}: CompanyMobileViewProps) {
  const [isFavorite, setIsFavorite] = useState(false);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: company.name,
          text:
            company.description ||
            `Confira ${company.name} no Santa Fé Conecta`,
          url: window.location.href,
        });
      } catch {
        // User cancelled
      }
    }
  };

  const hasContactInfo =
    company.phone || company.whatsapp || company.email || company.website;
  const hasAddressInfo = company.address || company.city;

  return (
    <div className="md:hidden bg-gray-50 pb-24">
      {/* Hero Section with Parallax Effect */}
      <div className="relative">
        {/* Background Image */}
        <div className="relative h-72 w-full overflow-hidden">
          <Image
            src={
              company.thumbnail
                ? resolveImageUrl(company.thumbnail)
                : "/bg-default-companies.avif"
            }
            alt={company.name}
            fill
            className="object-cover"
            priority
            unoptimized={!!company.thumbnail}
          />
          <div className="absolute inset-0 bg-linear-to-b from-black/50 via-black/30 to-gray-50" />
        </div>

        {/* Top Navigation Bar */}
        <div className="absolute top-0 left-0 right-0 z-20">
          <div className="flex items-center justify-between p-4 pt-safe">
            <Link
              href="/empresas"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsFavorite(!isFavorite)}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all"
              >
                {isFavorite ? (
                  <HeartSolidIcon className="w-5 h-5 text-red-500" />
                ) : (
                  <HeartIcon className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={handleShare}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-all"
              >
                <ShareIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Logo Card - Floating */}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-16 z-10">
          <div className="relative">
            <div className="w-32 h-32 rounded-3xl bg-white shadow-2xl ring-4 ring-white overflow-hidden">
              {company.logo ? (
                <Image
                  src={resolveImageUrl(company.logo)}
                  alt={`Logo ${company.name}`}
                  fill
                  className="object-cover rounded-3xl"
                  priority
                  unoptimized
                />
              ) : (
                <div className="w-full h-full bg-linear-to-br from-mygreen to-mygreen/80 flex items-center justify-center">
                  <span className="text-5xl font-bold text-white">
                    {company.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            {company.verified && (
              <div className="absolute -bottom-2 -right-2 bg-mygreen text-white p-2 rounded-full shadow-lg">
                <CheckBadgeIcon className="w-5 h-5" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="pt-20 px-4 space-y-4">
        {/* Company Name & Badge */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
          {company.verified && (
            <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 bg-mygreen/10 text-mygreen rounded-full text-sm font-medium">
              <CheckBadgeIcon className="w-4 h-4" />
              Empresa Verificada
            </div>
          )}
        </div>

        {/* Quick Action Buttons */}
        <div className="grid grid-cols-4 gap-3 py-2">
          {company.whatsapp && (
            <a
              href={`https://wa.me/55${company.whatsapp.replace(/\D/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-green-500 text-white flex items-center justify-center">
                <ChatBubbleLeftRightIcon className="w-6 h-6" />
              </div>
              <span className="text-xs font-medium">WhatsApp</span>
            </a>
          )}
          {company.phone && (
            <a
              href={`tel:${company.phone.replace(/\D/g, "")}`}
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-500 text-white flex items-center justify-center">
                <PhoneIcon className="w-6 h-6" />
              </div>
              <span className="text-xs font-medium">Ligar</span>
            </a>
          )}
          {company.website && (
            <a
              href={addUtmToWebsite(company.website)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-500 text-white flex items-center justify-center">
                <GlobeAltIcon className="w-6 h-6" />
              </div>
              <span className="text-xs font-medium">Site</span>
            </a>
          )}
          {company.email && (
            <a
              href={`mailto:${company.email}`}
              className="flex flex-col items-center gap-1.5 p-3 rounded-2xl bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-500 text-white flex items-center justify-center">
                <EnvelopeIcon className="w-6 h-6" />
              </div>
              <span className="text-xs font-medium">Email</span>
            </a>
          )}
        </div>

        {/* Description Card */}
        {company.description && (
          <div className="bg-white rounded-3xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Sobre
            </h3>
            <div
              className="text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: company.description }}
            />
          </div>
        )}

        {/* Categories Section */}
        {company.categories && company.categories.length > 0 && (
          <div className="hidden bg-white rounded-3xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Categorias
            </h3>
            <div className="flex flex-wrap gap-2">
              {company.categories.map((category) => (
                <Link
                  key={category.id}
                  href={`/categoria/${category.slug}`}
                  className="px-4 py-2 bg-linear-to-r from-mygreen/10 to-mygreen/5 text-mygreen rounded-xl text-sm font-medium hover:from-mygreen hover:to-mygreen hover:text-white transition-all"
                >
                  {category.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Contact Info List */}
        {hasContactInfo && (
          <div className="bg-white rounded-3xl overflow-hidden shadow-sm">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider px-5 pt-5 pb-3">
              Contato
            </h3>
            <div className="divide-y divide-gray-100">
              {company.phone && (
                <a
                  href={`tel:${company.phone.replace(/\D/g, "")}`}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
                    <PhoneIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Telefone</p>
                    <p className="text-gray-900 font-medium truncate">
                      {company.phone}
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </a>
              )}
              {company.whatsapp && (
                <a
                  href={`https://wa.me/55${company.whatsapp.replace(
                    /\D/g,
                    ""
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
                    <ChatBubbleLeftRightIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">WhatsApp</p>
                    <p className="text-gray-900 font-medium truncate">
                      {company.whatsapp}
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </a>
              )}
              {company.email && (
                <a
                  href={`mailto:${company.email}`}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
                    <EnvelopeIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">E-mail</p>
                    <p className="text-gray-900 font-medium truncate">
                      {company.email}
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </a>
              )}
              {company.website && (
                <a
                  href={addUtmToWebsite(company.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
                    <GlobeAltIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Website</p>
                    <p className="text-gray-900 font-medium truncate">
                      {company.website.replace(/^https?:\/\//, "")}
                    </p>
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Location Section */}
        {hasAddressInfo && (
          <div className="bg-white rounded-3xl overflow-hidden shadow-sm">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider px-5 pt-5 pb-3">
              Localização
            </h3>

            {/* Address Info */}
            <div className="px-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                  <MapPinIcon className="w-5 h-5" />
                </div>
                <div>
                  {company.address && (
                    <p className="text-gray-900 font-medium">
                      {company.address}
                    </p>
                  )}
                  {(company.city || company.state) && (
                    <p className="text-gray-500 text-sm mt-0.5">
                      {[company.city, company.state]
                        .filter(Boolean)
                        .join(" - ")}
                    </p>
                  )}
                  {company.zip_code && (
                    <p className="text-gray-500 text-sm">
                      CEP: {company.zip_code}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Map Preview */}
            {company.address && company.city && (
              <>
                <div className="w-full aspect-video relative">
                  <iframe
                    className="w-full h-full"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps/embed/v1/place?key=${
                      process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
                      "AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8"
                    }&q=${encodeURIComponent(
                      `${company.address}, ${company.city}, ${
                        company.state || "São Paulo"
                      }`
                    )}`}
                  />
                </div>
                <div className="p-4">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      `${company.address}, ${company.city}, ${
                        company.state || "São Paulo"
                      }`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-mygreen text-white py-3 rounded-xl font-semibold hover:bg-mygreen/90 transition-colors"
                  >
                    <MapPinIcon className="w-5 h-5" />
                    Como Chegar
                  </a>
                </div>
              </>
            )}
          </div>
        )}

        {/* Opening Hours */}
        {formattedHours && (
          <div className="bg-white rounded-3xl p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                <ClockIcon className="w-5 h-5" />
              </div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                Horário de Funcionamento
              </h3>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4">
              <pre className="text-gray-700 text-sm whitespace-pre-line font-sans leading-relaxed">
                {formattedHours}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      {company.whatsapp && (
        <div className="fixed bottom-20 right-4 z-30">
          <a
            href={`https://wa.me/55${company.whatsapp.replace(/\D/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-4 bg-green-500 text-white rounded-full shadow-2xl hover:bg-green-600 transition-all hover:scale-105"
          >
            <ChatBubbleLeftRightIcon className="w-6 h-6" />
            <span className="font-semibold">Conversar</span>
          </a>
        </div>
      )}
    </div>
  );
}
